import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactModal from '../../components/feature/ContactModal';
import SEO from '../../components/feature/SEO';

const stats = [
  { value: '$16M+', label: 'In fraud prevented' },
  { value: '1,800+', label: 'Businesses protected' },
  { value: '99.999%', label: 'Cryptographic security' },
  { value: '5 mins', label: 'Setup time' },
];

const painPoints = [
  {
    icon: 'ri-mail-line',
    title: 'Intercepted invoices',
    description: 'Cybercriminals intercept your invoice emails and replace your bank details before the email reaches your client.',
  },
  {
    icon: 'ri-file-copy-line',
    title: 'Perfect forgeries',
    description: 'Fraudsters replicate your exact invoice layout — same logo, same wording — with their account details substituted in.',
  },
  {
    icon: 'ri-bank-line',
    title: 'Misdirected payments',
    description: 'Your client pays in good faith. The money lands in a fraudster\'s account. You discover it weeks later — and the relationship is damaged.',
  },
];

const accountantBenefits = [
  'Offer a differentiated, high-value service your competitors can\'t match',
  'Protect all your clients\' QuickBooks invoices under one ProAdvisor plan',
  'Earn recurring commission as a QBOVerifier affiliate partner',
  'Up to 10 client QuickBooks realms under a single subscription',
  'White-glove onboarding support for you and your clients',
  'Be the trusted advisor who prevented a fraud — not the one who missed it',
];

const partnerBenefits = [
  'Refer clients and earn 25% commission in year one, 15% recurring',
  'Co-branded marketing materials provided at no cost',
  'Dedicated partner portal and performance dashboard',
  '$500 bonus when you reach 10 active client referrals',
  'No upfront costs or minimum commitments',
  'Monthly commission payments, fully transparent reporting',
];

const testimonials = [
  {
    quote: "One of my clients was nearly defrauded by a $28,000 fake invoice. QBOVerifier's bioQR stamp would have stopped it cold. I've now rolled it out across all my QuickBooks clients.",
    name: 'Sarah M.',
    role: 'CPA, Small Business Practice',
  },
  {
    quote: "As a bookkeeper managing 30 clients, the ProAdvisor plan is an absolute no-brainer. The protection it provides and the peace of mind for my clients is worth every cent.",
    name: 'James T.',
    role: 'Senior Bookkeeper & QuickBooks ProAdvisor',
  },
  {
    quote: "I read Harry\'s post, installed it the same day. Setup took four minutes. That was three months ago — I\'ve stamped 340 invoices since. Not a single fraud attempt has got through.",
    name: 'Michelle R.',
    role: 'Freelance Consultant & QBO User',
  },
];

const steps = [
  {
    number: '01',
    title: 'Install the Chrome Extension',
    description: 'A 60-second install into your Chrome browser. No IT department needed.',
  },
  {
    number: '02',
    title: 'Connect to QuickBooks Online',
    description: 'One-click authorisation. QBOVerifier integrates directly — no data ever leaves your environment.',
  },
  {
    number: '03',
    title: 'Send invoices as normal',
    description: 'Every invoice you send is automatically stamped with an AES-256 encrypted bioQR verification code.',
  },
  {
    number: '04',
    title: 'Your clients verify in seconds',
    description: 'Clients scan the QR code or use the free browser extension. Instant confirmation — this is genuine, from you, untampered.',
  },
];

export default function LinkedInLanding() {
  const [activeTab, setActiveTab] = useState<'accountants' | 'partners'>('accountants');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://qboverifier.com')}`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="QBOVerifier – Invoice Fraud Protection for QuickBooks | From LinkedIn"
        description="Invoice fraud costs businesses billions annually. QBOVerifier adds AES-256 cryptographic stamps to every QuickBooks invoice – impossible to forge. Setup in 5 minutes. Join 1,800+ protected businesses."
        keywords="QuickBooks invoice fraud, invoice verification LinkedIn, business email fraud prevention, AES-256 invoice protection, QBOVerifier"
        canonical="/from-linkedin"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'QBOVerifier – Invoice Fraud Protection for QuickBooks',
          description: 'Cryptographic invoice fraud protection for QuickBooks Online users. AES-256 encryption on every invoice.',
        }}
      />
      <Header />

      {/* Hero */}
      <section
        className="relative pt-24 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0c2340 100%)' }}
      >
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, #29B3E6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #29B3E6 0%, transparent 40%)',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div
            className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {/* LinkedIn badge */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/80 px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                <i className="ri-linkedin-fill text-[#0A66C2] text-base w-4 h-4 flex items-center justify-center"></i>
                You came from LinkedIn — welcome. Here's the full story.
              </div>
            </div>

            <h1 className="text-center text-4xl md:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
              Invoice Fraud Is Happening<br />
              <span style={{ color: '#29B3E6' }}>To Businesses Like Yours.</span><br />
              Right Now.
            </h1>

            <p className="text-center text-xl text-white/70 max-w-3xl mx-auto mb-10 leading-relaxed">
              QBOVerifier is the world's first cryptographic invoice verification system built specifically for QuickBooks Online. 
              Military-grade AES-256 encryption. Impossible to forge. 5 minutes to protect every invoice you send.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#29B3E6' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E9BD1')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#29B3E6')}
              >
                <i className="ri-shield-check-line"></i>
                See Plans &amp; Pricing
              </Link>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                <i className="ri-mail-send-line"></i>
                Talk to the Team
              </button>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center border border-white/10 rounded-xl p-5 bg-white/5 backdrop-blur-sm">
                  <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-white/60 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
              The Threat Is Real — And It's Invisible
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Invoice fraud costs global businesses over <strong>$26 billion annually</strong>. 
              QuickBooks Online users are a prime target — and most victims don't know it happened until weeks later.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            {painPoints.map((point) => (
              <div key={point.title} className="bg-white rounded-xl p-7 border border-red-100">
                <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mb-5">
                  <i className={`${point.icon} text-red-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{point.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-8 text-white text-center">
            <p className="text-xl font-semibold leading-relaxed max-w-3xl mx-auto">
              "Most business owners discover invoice fraud the same way — <em>after it's already happened.</em> 
              A client calls. They say they paid. You check your accounts. Nothing."
            </p>
            <p className="mt-4 text-white/80 text-sm">— Harry Hollander, Founder, QBOVerifier &amp; Ardeun Biometrics</p>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div
                className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
                style={{ backgroundColor: '#E8F7FC', color: '#29B3E6' }}
              >
                The QBOVerifier Solution
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                A Cryptographic Stamp on Every Invoice. Impossible to Fake.
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed mb-6">
                QBOVerifier's patented Chrome extension integrates directly with QuickBooks Online. 
                The moment you send an invoice, it's automatically embedded with a unique <strong>bioQR verification stamp</strong> — 
                powered by AES-256 military-grade encryption.
              </p>
              <p className="text-gray-500 text-lg leading-relaxed mb-8">
                The stamp is mathematically impossible to replicate, forge, or tamper with. 
                Your clients verify any invoice in seconds — one scan, instant confidence.
              </p>
              <div className="space-y-3">
                {[
                  'Automatic — stamps every invoice, no manual steps',
                  'AES-256 encryption, same class as military and banking systems',
                  'Client verification takes under 10 seconds',
                  'No data ever leaves your QuickBooks environment',
                  '5-minute install, zero IT knowledge required',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#29B3E6' }}
                    >
                      <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20a%20professional%20business%20invoice%20document%20with%20a%20glowing%20security%20QR%20code%20stamp%20in%20the%20corner%2C%20dark%20background%20with%20soft%20teal%20and%20cyan%20cryptographic%20patterns%2C%20high-tech%20cybersecurity%20visualization%2C%20clean%20and%20minimal%20design%20with%20strong%20contrast%20between%20the%20glowing%20stamp%20and%20dark%20professional%20document%20background%2C%20ultra-modern%20premium%20corporate%20aesthetic&width=600&height=500&seq=li-sol1&orientation=portrait"
                alt="QBOVerifier bioQR stamp on an invoice"
                className="rounded-2xl object-cover w-full h-[480px]"
              />
              <div
                className="absolute -bottom-5 -left-5 bg-white rounded-xl p-4 border max-w-xs"
                style={{ borderColor: '#29B3E6' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#29B3E6' }}
                  >
                    <i className="ri-shield-check-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">Invoice Verified</div>
                    <div className="text-xs text-gray-400">Genuine &amp; untampered — confirmed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
              Up and Running in 5 Minutes
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              No complex setup. No IT department. Just open Chrome, install the extension, and you're protecting every invoice automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step) => (
              <div key={step.number} className="bg-white rounded-xl p-6 border border-gray-100 relative">
                <div className="text-5xl font-bold mb-4" style={{ color: '#E8F7FC', fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                  {step.number}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accountants / Partners Tab Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
              Built for Professionals Like You
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Whether you're an accountant protecting your client base, or a consultant building a referral income stream — QBOVerifier has a programme for you.
            </p>
          </div>

          {/* Tab Switch */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-full p-1.5" style={{ backgroundColor: '#29B3E6' }}>
              <button
                onClick={() => setActiveTab('accountants')}
                className={`px-7 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'accountants' ? 'bg-white text-gray-900' : 'text-white hover:text-white/80'
                }`}
                style={activeTab === 'accountants' ? { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' } : {}}
              >
                Accountants &amp; ProAdvisors
              </button>
              <button
                onClick={() => setActiveTab('partners')}
                className={`px-7 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'partners' ? 'bg-white text-gray-900' : 'text-white hover:text-white/80'
                }`}
                style={activeTab === 'partners' ? { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' } : {}}
              >
                Referral Partners
              </button>
            </div>
          </div>

          {activeTab === 'accountants' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div
                  className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
                  style={{ backgroundColor: '#E8F7FC', color: '#29B3E6' }}
                >
                  ProAdvisor Programme
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                  Protect Your Clients Before They Become a Statistic
                </h3>
                <p className="text-gray-500 leading-relaxed mb-6">
                  As a QuickBooks ProAdvisor or accounting professional, your clients trust your recommendations completely. 
                  QBOVerifier lets you deliver invoice fraud protection as a first-class advisory service — 
                  covering all your client realms under a single ProAdvisor plan.
                </p>
                <ul className="space-y-3 mb-8">
                  {accountantBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: '#29B3E6' }}
                      >
                        <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/affiliates/accountants"
                    className="inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                    style={{ backgroundColor: '#29B3E6' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E9BD1')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#29B3E6')}
                  >
                    <i className="ri-user-star-line"></i>
                    Learn About the ProAdvisor Plan
                  </Link>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center justify-center gap-2 border-2 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm hover:bg-gray-50"
                    style={{ borderColor: '#29B3E6' }}
                  >
                    <i className="ri-mail-line"></i>
                    Speak to the Team
                  </button>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20accountant%20or%20bookkeeper%20in%20a%20modern%20office%20reviewing%20financial%20reports%20on%20a%20laptop%2C%20warm%20natural%20light%2C%20clean%20and%20organized%20desk%2C%20confident%20and%20focused%20professional%20expression%2C%20sophisticated%20minimalist%20workspace%20environment%20with%20soft%20neutral%20tones%20and%20subtle%20green%20plants%20in%20background&width=600&height=500&seq=li-acc1&orientation=portrait"
                  alt="Accountant reviewing client invoices with QBOVerifier"
                  className="rounded-2xl object-cover w-full h-[440px]"
                />
              </div>
            </div>
          )}

          {activeTab === 'partners' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div
                  className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
                  style={{ backgroundColor: '#FFF7ED', color: '#EA580C' }}
                >
                  Referral Partner Programme
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                  Turn Your Network Into a Revenue Stream
                </h3>
                <p className="text-gray-500 leading-relaxed mb-6">
                  If you work with small businesses, consultants, or QuickBooks users — you're sitting on an untapped referral 
                  opportunity. Our partner programme is simple: you refer, we convert, you earn — month after month.
                </p>
                <ul className="space-y-3 mb-8">
                  {partnerBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/affiliates/partners"
                    className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-links-line"></i>
                    Join the Partner Programme
                  </Link>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center justify-center gap-2 border-2 border-orange-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm hover:bg-orange-50"
                  >
                    <i className="ri-mail-line"></i>
                    Get in Touch
                  </button>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20business%20professionals%20in%20a%20modern%20open-plan%20office%20having%20a%20collaborative%20conversation%20about%20a%20referral%20partnership%2C%20one%20showing%20a%20laptop%20screen%20with%20revenue%20charts%20and%20data%2C%20confident%20handshake%20energy%2C%20bright%20contemporary%20workspace%20with%20warm%20natural%20lighting%20and%20clean%20neutral%20tones%2C%20premium%20corporate%20photography%20style&width=600&height=500&seq=li-part1&orientation=portrait"
                  alt="Partners discussing QBOVerifier referral programme"
                  className="rounded-2xl object-cover w-full h-[440px]"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
              What Users Are Saying
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-xl p-7 border border-gray-100">
                <i className="ri-double-quotes-l text-3xl mb-4 block" style={{ color: '#29B3E6' }}></i>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">{t.quote}</p>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{t.name}</div>
                  <div className="text-gray-400 text-xs mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Harry */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <img
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/46b541c40c6dbb939e5607c98f7fb782.jpeg"
                alt="Harry Hollander, Founder of QBOVerifier"
                className="rounded-2xl object-contain w-full h-auto"
              />
            </div>
            <div>
              <div
                className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
                style={{ backgroundColor: '#E8F7FC', color: '#29B3E6' }}
              >
                The Founder
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
                40 Years Building the Security Systems That Protect Financial Transactions
              </h2>
              <p className="text-gray-500 leading-relaxed mb-4">
                Harry Hollander has spent four decades at the intersection of cryptography, biometrics, and enterprise security — 
                working with Fortune 500 companies, ASX-listed corporations, and government agencies across three continents.
              </p>
              <p className="text-gray-500 leading-relaxed mb-4">
                After watching invoice fraud devastate small business clients repeatedly — and realising that QuickBooks Online and Xero users had little native protection — Harry built bioVerifier to solve the problem once and for all. Not only for these accounting frameworks, but also for Email and other similar systems.
              </p>
              <p className="text-gray-500 leading-relaxed">
                The result is the only patented, cryptographic invoice fraud prevention system designed specifically for 
                QuickBooks Online. Not an add-on. Not a workaround. A purpose-built solution backed by the same class 
                of encryption that protects military and banking systems.
              </p>
            </div>
          </div>

          {/* Credential badges — full-width horizontal strip below the two columns */}
          <div className="flex gap-3 mt-10">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 leading-tight">40+</div>
              <div className="text-xs text-gray-500 mt-0.5">Years experience</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 leading-tight">IBM</div>
              <div className="text-xs text-gray-500 mt-0.5">Former engineer</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 leading-tight">F500</div>
              <div className="text-xs text-gray-500 mt-0.5">Executive background</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 leading-tight">ASX Listed</div>
              <div className="text-xs text-gray-500 mt-0.5">Executive background</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center flex-[1.5] min-w-0">
              <div className="text-base font-bold text-gray-900 leading-tight">Patented and Patent Pending</div>
              <div className="text-xs text-gray-500 mt-0.5">Technology</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0c2340 100%)' }}
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-5" style={{ fontFamily: 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif' }}>
            You Read the Post. Now Take the Step.
          </h2>
          <p className="text-white/70 text-lg mb-10 leading-relaxed">
            Every invoice you send without QBOVerifier is an invoice that could be intercepted, faked, and misdirected. 
            1,800+ businesses are already protected. Join them today — setup takes five minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 text-white font-bold px-8 py-4 rounded-lg text-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#29B3E6' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E9BD1')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#29B3E6')}
            >
              <i className="ri-rocket-line"></i>
              Start Free — No Credit Card
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              <i className="ri-calendar-line"></i>
              Book a 15-Min Demo
            </button>
          </div>

          {/* LinkedIn share */}
          <div className="border-t border-white/10 pt-8">
            <p className="text-white/50 text-sm mb-4">Did this resonate? Share it with your network.</p>
            <a
              href={linkedInShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#0958A8] text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-linkedin-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              Share on LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Let's Talk — QBOVerifier Team"
        subtitle="Leave your details and we'll be in touch within one business day"
      />
    </div>
  );
}
